import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Tag } from "@carbon/react";
import { Information } from "@carbon/icons-react";
import {
  DataTableColumn,
  DataTableRow,
} from "../../components/carbon/DataTable/DataTable";
import {
  PurchaseRequestItemState,
  PurchaseRequestItemV2,
  PurchaseRequestStatus,
  PurchaseRequestV2,
} from "../../types";
import { cutIdOnFirstHyphen, formatDate } from "../../utils";
import {
  getActiveItemsCount,
  getAssignedBuyerNames,
  getAwardedItemsCount,
  getDisplayStatus,
  getItemRequestId,
  getRequiredDate,
  getRequesterName,
} from "./purchaseRequestListHelpers";
import { TFunc } from "./purchaseRequestListTypes";

/**
 * Row/header mappers for the PR list's two DataTable instances (requests view, items view) —
 * split out of `PurchaseRequestsV2.tsx` per Task 4's <400-line-per-file budget, mirroring
 * `containers/Materials/materialRows.tsx`.
 */

const statusTagType: Record<
  PurchaseRequestStatus | PurchaseRequestItemState,
  "gray" | "blue" | "purple" | "teal" | "green" | "cyan" | "red"
> = {
  [PurchaseRequestStatus.DRAFT]: "gray",
  [PurchaseRequestStatus.IN_APPROVAL]: "blue",
  [PurchaseRequestStatus.QUOTING]: "purple",
  [PurchaseRequestStatus.PARTIALLY_AWARDED]: "teal",
  [PurchaseRequestStatus.AWARDED]: "green",
  [PurchaseRequestStatus.PENDING_DELIVERY]: "cyan",
  [PurchaseRequestStatus.DELIVERED]: "green",
  [PurchaseRequestStatus.REJECTED]: "red",
};

/** Same label i18n key the pre-Carbon `Pill` component used
 * (`purchaseRequests.status.<lowercased state>`, e.g. `in_approval` -> "Created") — only the
 * container (a Carbon `Tag`, colour-mapped above) changes, not the text. */
export const statusTag = (
  state: PurchaseRequestStatus | PurchaseRequestItemState,
  t: TFunc
): ReactNode => (
  <Tag type={statusTagType[state] ?? "gray"} size="sm">
    {t(`purchaseRequests.status.${state.toLowerCase()}`, {
      defaultValue: state,
    })}
  </Tag>
);

export interface PurchaseRequestRow extends DataTableRow {
  id: string;
  code: string;
  name: string;
  requester: string;
  assignedBuyer: string;
  createdAt: string;
  neededTo: string;
  itemsCount: string;
  status: ReactNode;
}

export const requestHeaders = (
  t: TFunc
): DataTableColumn<PurchaseRequestRow>[] => [
  { key: "code", header: t("purchaseRequests.fields.purchaseRequestCode") },
  { key: "name", header: t("purchaseRequests.columns.name") },
  { key: "requester", header: t("purchaseRequests.columns.requester") },
  { key: "assignedBuyer", header: t("purchaseRequests.columns.assignedBuyer") },
  { key: "createdAt", header: t("purchaseRequests.columns.createdAt") },
  { key: "neededTo", header: t("purchaseRequests.columns.neededTo") },
  { key: "itemsCount", header: t("purchaseRequests.columns.items") },
  { key: "status", header: t("purchaseRequests.columns.status") },
];

/** `onRowClick` (the container's own `navigate` call) covers the pre-Carbon whole-row click, so
 * no cell here needs its own `Link`. */
export const mapRequestToRow = (
  request: PurchaseRequestV2,
  t: TFunc
): PurchaseRequestRow => {
  const requiredDate = getRequiredDate(request);
  return {
    id: request.id,
    code: request.purchaseRequestCode || "-",
    name: request.name || "-",
    requester: getRequesterName(request),
    assignedBuyer: getAssignedBuyerNames(request).join(", ") || "-",
    createdAt: formatDate(request.createdAt),
    neededTo: requiredDate ? formatDate(requiredDate) : "-",
    itemsCount: `${getAwardedItemsCount(request)}/${getActiveItemsCount(request)}`,
    status: statusTag(getDisplayStatus(request), t),
  };
};

export interface PurchaseRequestItemRow extends DataTableRow {
  id: string;
  code: ReactNode;
  ean: ReactNode;
  name: ReactNode;
  requestingCenter: string;
  assignedBuyer: string;
  createdAt: string;
  status: ReactNode;
}

export const itemHeaders = (
  t: TFunc
): DataTableColumn<PurchaseRequestItemRow>[] => [
  { key: "code", header: t("purchaseRequests.fields.purchaseRequestCode") },
  { key: "ean", header: t("purchaseRequests.items.columns.id") },
  { key: "name", header: t("purchaseRequests.columns.name") },
  {
    key: "requestingCenter",
    header: t("purchaseRequests.items.columns.requestingCenter"),
  },
  { key: "assignedBuyer", header: t("purchaseRequests.columns.assignedBuyer") },
  { key: "createdAt", header: t("purchaseRequests.columns.createdAt") },
  { key: "status", header: t("purchaseRequests.columns.status") },
];

/**
 * The items-view table keeps its pre-Carbon per-cell navigation (the code/EAN cells link to the
 * item's own request; the request-request-wide row click the requests view uses doesn't apply
 * here, since this row's own "select" checkbox already claims the row) plus the material-info
 * trigger button next to the item's name.
 */
export const mapItemToRow = (
  item: PurchaseRequestItemV2,
  t: TFunc,
  onOpenInfo: (item: PurchaseRequestItemV2) => void
): PurchaseRequestItemRow => {
  const requestId = getItemRequestId(item);
  const requestLink = (label: ReactNode): ReactNode =>
    requestId ? (
      <Link to={`/purchase-requests/${requestId}`}>{label}</Link>
    ) : (
      label
    );

  return {
    id: item.id,
    code: requestLink(cutIdOnFirstHyphen(requestId ?? undefined) || "-"),
    ean: requestLink(item.material?.ean || "-"),
    name: (
      <span className="purchase-requests__item-name">
        {requestLink(item.material?.globalMaterialName || "-")}
        {item.material ? (
          <button
            type="button"
            className="purchase-requests__info-trigger"
            onClick={() => onOpenInfo(item)}
            aria-label={t("purchaseRequests.items.materialInfo")}
            title={t("purchaseRequests.items.materialInfo")}
          >
            <Information size={16} />
          </button>
        ) : null}
      </span>
    ),
    requestingCenter: item.requestingSite?.internalCode || "-",
    assignedBuyer: item.assignedBuyer?.fullName || "-",
    createdAt: item.createdAt ? formatDate(item.createdAt) : "-",
    status: statusTag(item.state, t),
  };
};
